/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import {
  cloneDeep,
  concat,
  differenceBy,
  each,
  endsWith,
  filter,
  find,
  forEach,
  get,
  invoke,
  isArray,
  isEmpty,
  isNumber,
  isObject,
  map,
  remove,
  replace,
  set
} from 'lodash';
import { NgForm } from '@angular/forms';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatTabChangeEvent } from '@angular/material/tabs';

import { ApiService } from '@nrwl/common-lib';

const multiSelect = 'multi-select';

export const REMOVED = '!REMOVED!';

export class BaseWizardStep {
  // REMOVED: string;
  defaultId: string;
  config: any;
  parent: any;
  wizardService: any;
  sharedOutput: any;
  id: any;
  selected = false;
  dataSource: any;
  dataSourceObservable: BehaviorSubject<any>;
  selectedAll = false;
  filterText: string;
  selectedTab: number;
  defaultParamsById: any;
  form: NgForm;
  isLoading = false;
  itemSelected = false;
  output: any;
  inputs: any;
  requests: any;
  stateOfExtendedControls: any;
  subContent: any;
  selectedItem: any;
  disableBackBtn = false;
  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(readonly apiService: ApiService, config: any, parent: any) {
    // this.REMOVED = "!REMOVED!";
    this.defaultId = 'wizardStep';

    this.config = config;
    this.parent = parent;
    this.wizardService = this.findWizardService(this.parent);

    this.sharedOutput = parent.sharedOutput;
    this.id = config.id;
    this.selected = false;
    this.dataSource = [];
    this.dataSourceObservable = new BehaviorSubject<any>([]);
    this.selectedAll = false;
    this.filterText = '';
    this.selectedTab = 0;
    this.defaultParamsById = {};
    // TODO: original code was using plain {}. reactive form must be initialized..
    //this.form = null;
    this.isLoading = false;
    this.itemSelected = false;

    this.output = this._generateOutputs();

    this.inputs = this._generateInputs();

    this.requests = [];

    // stores data, submitted via dataValueChange, metaDataValueChange methods
    // used for isValid() and page reset purposes
    this.stateOfExtendedControls = {};
  }

  //--------------------------------------- Set prop on step init ------------------------------------------------------

  _setOutputBySendParam(outputParam) {
    switch (outputParam.type) {
      case 'select':
        return this._selectParam(this.dataSource, outputParam.data.id);
      case multiSelect:
        return this._selectParams(this.dataSource, outputParam.data);
      default:
        return outputParam;
    }
  }

  setDefaultValues(dataSource, dataSourceKey = 'id', filterDataSourceKey) {
    forEach(this.output, (output, key) => {
      if (!isEmpty(this.output[key])) {
        return;
      }

      const defaultParams = this.parent.defaultParamsById[key];
      if (!isEmpty(defaultParams)) {
        let tempOutput = defaultParams;

        if (isArray(defaultParams) && key === dataSourceKey) {
          tempOutput = [];

          if (isArray(dataSource)) {
            tempOutput = tempOutput.concat(
              this.getDefaultListItems(dataSource, filterDataSourceKey, defaultParams)
            );
            this.selectedAll = dataSource.length === tempOutput.length;
          } else {
            forEach(dataSource, dataSourceItem => {
              tempOutput = tempOutput.concat(
                this.getDefaultListItems(dataSourceItem, filterDataSourceKey, defaultParams)
              );
            });
          }
        }
        this.output[key] = tempOutput;
      }
    });
  }

  getDefaultListItems(dataSourceItem, key, defaultParams) {
    return filter(dataSourceItem, (item: any) => {
      const defaultParamsItem = find(defaultParams, p => p[key] === item[key]);
      if (defaultParamsItem) {
        item.isChecked = true;
        item.disabled = get(defaultParamsItem, 'disabled', false);
      }
      return item.isChecked;
    });
  }

  //------------------------------------------- Step Control -----------------------------------------------------------

  isValid() {
    const isValidStandardControls =
      (isEmpty(this.form) || this.form.valid) &&
      !find(this.config.def.outputs, key => {
        return (
          this._getIndex(key, this.config.def.outputOptional) === -1 &&
          this.getVolume(this.output[key])
        );
      });
    let isValidExtendedControls = true;
    forEach(this.stateOfExtendedControls, (value, key) => {
      isValidExtendedControls = isValidExtendedControls && value.isValid;
    });

    return isValidStandardControls && isValidExtendedControls;
  }

  private getVolume(item) {
    if (isNumber(item)) {
      return false;
    } else return isEmpty(item);
  }

  _getIndex(item, array = []) {
    return array.indexOf(item);
  }

  onNext() {
    forEach(this.config.outputShared, key => {
      this.sharedOutput[key] = this.output[key];
    });
    this.copyProperties(this.parent.output, this.output);
    this.selected = true;
  }

  onBack() {
    this.copyProperties(this.parent.output, this.output);
    //To retain the inputs given even the user goes back to the previous step in edit mode
    //this._resetStep();
  }

  _resetStep() {
    this._resetOutputs();
    this.output = this._generateOutputs();

    //Reset inputs
    this.inputs = this._generateInputs();
    this.filterText = '';

    if (this.subContent) {
      this.subContent._resetStep();
    }
    this.selectedTab = 0;
    this.itemSelected = false;
    this.toggleSubContent(false);
  }

  _resetOutputs(outputKeys = this.config.def.outputs) {
    forEach(outputKeys, key => {
      if (isArray(this.output[key])) {
        this.output[key].length = 0;
      } else {
        if (this.output[key]) {
          if (this.output[key].selected !== undefined) {
            this.output[key].selected = false;
          }
          this.output[key] = null;
        }
      }
    });
  }

  clearSkippedPageOutput() {
    this._resetStep();
    this._resetOutputsFromExtendedControls();
    this.copyProperties(this.parent.output, this.output);
  }

  _resetOutputsFromExtendedControls() {
    forEach(this.stateOfExtendedControls, (value, key) => {
      if (value.meta) {
        this._changeMetaDataValue(value.key, REMOVED);
      } else {
        this._changeSimpleValue(value.key, REMOVED);
      }
    });
  }

  toggleSubContent(value?, templateModel = this.subContent) {
    this.parent.toggleSubContent(value, templateModel);
  }

  tabChanged(event: MatTabChangeEvent) {
    this.selectedTab = event.index;
  }

  //----------------------------------------- Inputs/Outputs -----------------------------------------------------------

  _generateInputs(
    required = this.config.def.inputsRequired,
    optional = this.config.def.inputsOptional,
    fixed = this.config.valuesFixed,
    valuesDefault = this.config.valuesDefault
  ) {
    const inputs = {};
    forEach(concat(required, optional), key => {
      if (isObject(key)) {
        forEach(key, (items, subKey) => {
          inputs[subKey] = {};
          forEach(items, (item: string) => {
            inputs[subKey][item] =
              fixed[item] || (valuesDefault ? cloneDeep(valuesDefault[item]) : null);
          });
        });
      } else {
        inputs[key] = fixed[key] || (valuesDefault ? cloneDeep(valuesDefault[key]) : null);
      }
    });
    return inputs;
  }

  _generateOutputs(config = this.config) {
    const output = {};
    const allOutputs = concat(config.def.outputs, config.def.outputOptional);
    forEach(allOutputs, key => {
      output[key] = this._getOutputItem(config, key);
    });
    return output;
  }

  _getOutputItem(config, output) {
    if (config.valuesDefault && config.valuesDefault[output]) {
      if (this._isFunctionName(config.valuesDefault[output])) {
        return this._invokeAsFunction(config.valuesDefault[output]);
      } else {
        return cloneDeep(config.valuesDefault[output]);
      }
    } else if (config.def.stepType === multiSelect) {
      return [];
    }

    return null;
  }

  _invokeAsFunction(functionName) {
    const name = replace(functionName, '()', '');
    return invoke(this, name);
  }
  // ----------------------------------------------Filters ------------------------------------------------

  get filterKeys() {
    return get(this.config.def, 'filterBy', []);
  }

  /**
   * Old filter was supporting lookup of values in just one field.
   * For example, check this data object: {'alias':'abc', 'location':{'name':'xyz'}}
   * A navigation (to get filterable field value) was specified like this: this.config.def.filterBy: ["location", "name"]
   * This navigation returns 'xyz', and then this value is compared against user input.
   *
   * New filter allows to search for user input in multiple fields (this functionality is based on get() from lodash).
   * Eg., if you want to use the same data object as shown above,
   * and compare content in 'filterText' against values in 'alias' and 'name' fields, then you must specify filter
   * config like this:
   * this.config.def.filterBy: ["location.name", "alias"]
   *
   */
  filterList(filterText) {
    if (isEmpty(this.filterKeys)) {
      return;
    }

    forEach(this.dataSource, group => {
      this._filter(filterText, group);
    });
  }

  _filter(filterText, group) {
    if (isArray(group.list)) {
      forEach(group.list, item => {
        this._filterItem(filterText, item);
      });
    } else {
      this._filterItem(filterText, group);
    }
  }

  _filterItem(filterText, item) {
    if (isEmpty(item)) {
      return;
    }
    const value = [];
    each(this.filterKeys, key => {
      value.push(get(item, key, ''));
    });

    const allFieldsJoined = value.join(' ').toLowerCase();
    if (isEmpty(filterText) || allFieldsJoined.indexOf(filterText.toLowerCase()) > -1) {
      item.hidden = false;
    } else {
      item.hidden = true;
    }
  }

  filterData(source, target, by) {
    return differenceBy(source, target, by);
  }

  //---------------------------------------- Request/Format Data -------------------------------------------------------

  _requestData(
    subPath,
    onSuccess = () => {
      // Empty function
    },
    key = null,
    method
  ) {
    if (isEmpty(this.config.def.dataUrl) || this._isEmptyFields()) {
      return;
    }

    this._requestSingleData(this._generateURL(subPath), key, onSuccess, method);
  }

  _generateURL(subPathProp) {
    const subPath = subPathProp || this.config.def.subPath || '';
    return this.config.def.dataUrl + subPath;
  }

  _requestSingleData(
    url,
    key,
    onSuccess = () => {
      // Empty function
    },
    method = 'get'
  ) {
    this.inputs = this.config.def.postParam ? this.config.def.postParam : this.inputs;
    this.isLoading = true;
    this.parent.isLoading = true;
    this.apiService[method + '$']<any>(url, this.inputs)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        const dataSource = this._formatData(response);
        this._onSuccessfulRequest(key, dataSource);
        onSuccess.bind(this)();
        this.isLoading = false;
        this.parent.isLoading = false;
      });

    this.requests.push(url);
  }

  _onSuccessfulRequest(key, dataSource) {
    this.setDefaultValues(
      dataSource,
      this.config.def.dataSourceKey,
      get(this.config, 'def.filterDataSource.key', 'id')
    );
    this.dataSource = dataSource;
    this.dataSourceObservable.next(this.dataSource);
  }

  clearAllRequests() {
    forEach(this.requests, url => {
      remove(this.requests, { url });
    });
  }

  _formatData(rawData) {
    if (rawData.success) {
      rawData = rawData.success;
    }
    if (isArray(rawData)) {
      forEach(rawData, group => {
        forEach(group.list, item => {
          item.disabled = this._isItemDisabled.bind(this, item);
        });
        return group;
      });
    }

    return rawData;
  }

  _isItemDisabled(item) {
    if (!this.config.def.disabled) {
      return false;
    }

    return (
      isEmpty(item) ||
      !find(this.parent.output[this.config.def.disabled.key], (parentItems): any => {
        return find(item.tests, {
          id: parentItems[this.config.def.disabled.prop]
        });
      })
    );
  }

  _isEmptyFields() {
    return find(this.inputs, (input, key) => {
      if (isEmpty(input) && !isEmpty(this.sharedOutput[key])) {
        const value = this.sharedOutput[key];
        if (isArray(value)) {
          this.inputs[key] = map(value, 'id');
        } else if (isObject(value)) {
          this.inputs[key] = (value as any).id;
        } else {
          this.inputs[key] = value;
        }

        return false;
      }

      return isEmpty(input);
    });
  }

  set(path, value) {
    set(this, path, value);
  }

  //------------------------------------------- Click Events -----------------------------------------------------------

  onItemClick(outputKey, item) {
    //This condition should be removed once windows service agent is ready
    this.itemSelected = true;
    return this._getClickOnItemFunction(this.config.def.stepType).bind(this)(outputKey, item);
  }

  onListItemClick(group) {
    if (!group.disabled) {
      group.isChecked = !group.isChecked;
      this._returnOutput('groupIds', this.dataSource, null, {
        isChecked: true
      });
    }
  }

  onChangeItemSelection(item) {
    this.stateOfExtendedControls = {};
    item.selected = false;
    this.itemSelected = false;
    this._resetStep();
  }

  _getClickOnItemFunction(stepType) {
    switch (stepType) {
      case 'single-select':
        return this._singleSelect;
      case 'return-output':
        return this._returnOutput;
      case multiSelect:
        return this._multiSelect;
      default:
        return null;
    }
  }

  _singleSelect(outputKey, item) {
    if (item.disabled && item.disabled()) {
      return;
    }

    if (!isEmpty(this.output[outputKey])) {
      this.output[outputKey].selected = false;
    }

    item.selected = true;
    this.output[outputKey] = item;
  }

  _returnOutput(outputKey, item, command, condition) {
    let returnItem = cloneDeep(item);
    if (condition) {
      returnItem = filter(returnItem, condition);
    }

    this.output[outputKey] = returnItem;
    if (command === 'append') {
      this.parent.output[outputKey] = this.parent.output[outputKey].concat(returnItem);
    } else {
      this.copyProperties(this.parent.output, this.output);
    }
    this.selectedAll = this.parent.output[outputKey].length === this.dataSource.length;
    this.toggleSubContent(false);
  }

  _multiSelect(outputKey, item) {
    item.selected = !item.selected;
    if (item.selected && this.output[outputKey].length > 0) {
      this.output[outputKey][0].selected = false;
      remove(this.output[outputKey], this.output[outputKey][0]);
    }

    if (item.selected) {
      this.output[outputKey].push(item);
    } else {
      remove(this.output[outputKey], item);
    }

    this.toggleSubContent(false);
  }

  selectAll(list, checked) {
    forEach(list, item => {
      if (!item.disabled) {
        item.isChecked = checked;
      }
    });
  }

  isAllSelected(key) {
    return this.dataSource.length > 0 && this.dataSource.length === this.output[key].length;
  }

  //--------------------------------------------- list actions ---------------------------------------------------------

  _selectParams(source, array) {
    const itemkey = 'userName';
    const ids = map(array, itemkey);

    return filter(source, item => {
      return ids.indexOf(item[itemkey]) > -1;
    });
  }

  _selectParam(source, id) {
    return find(source, { id });
  }

  //--------------------------------------------- Backend response -------------------------------------------------------

  _reqResOnSuccessful(response) {
    this.parent.onSuccess(response.data.success.agent);
  }

  _reqResOnFailded(response) {
    this.parent.isLoading = false;
    this.output.reason = response.success.reason;
    this.parent._handleErrorResponse(response, this.parent.onFail);
  }

  _changeMetaDataValue(key, newValue) {
    if (!this.output.metaData) {
      this.output.metaData = [];
    }
    const existingPair: any = find(this.output.metaData, {
      key
    });
    if (existingPair) {
      existingPair.value = newValue;
    } else {
      this.output.metaData.push({
        key,
        value: newValue
      });
    }
  }

  /**
   * angular.extend does shallow copy of keys from 'source' to 'target'.
   * This is fine for most use cases in context of wizard.
   * However it is bad for properties that are 'arrays' of key-value pairs.
   * Eg: target:
   * {
   *    name: 'test1',
   *    meta_data: [{key1: 'value1'}]
   * }
   * And source:
   * {
   *    name: 'test2',
   *    meta_data: [{key2: 'value2'}]
   * }
   * angular.extend will produce this output:
   * {
   *  name: 'test2', // OK
   *  meta_data: [{key2: 'value2'}] // totally NOT ok
   * }
   * Eg, meta_data array is populated with key-value pair in 'originator' and
   * then in 'destination' pages. As keys are different - we want to 'merge' arrays.
   *
   * Here is the logic to implement:
   * - enumerate keys from 'source', iterate over them
   * - if key is not 'meta_data', just copy it over from source to target
   * - if key is a 'meta_data', we trigger special function that intelligently merges entries in meta_data array
   *   by 'key' keys.
   *
   */
  copyProperties(target, source) {
    const keys = Object.keys(source);
    for (let j = 0, jj = keys.length; j < jj; j++) {
      const key = keys[j];
      const src = source[key];

      if (this._isMetaData(key)) {
        target[key] = this._mergeMetaData(target[key], src);
      } else if (src === REMOVED) {
        // make sure 'removed' key is destroyed in output
        delete target[key];
      } else {
        target[key] = src;
      }
    }
  }

  _isMetaData(key) {
    return key === 'metaData';
  }

  _mergeMetaData(src1 = [], src2 = []) {
    // copy over all keys from src1
    // then iterate over props from src2, search for matching entries by 'key' - and overwrite if found
    const result = [];
    if (src1 && this._isArray(src1)) {
      forEach(src1, s => {
        result.push(s);
      });
    }
    if (src2 && this._isArray(src2)) {
      forEach(src2, s => {
        if (s.value === REMOVED) {
          remove(result, {
            key: s.key
          });
        } else {
          // regular insert/update of data
          const match = find(result, {
            key: s.key
          });
          if (match) {
            match.value = s.value;
          } else {
            result.push(s);
          }
        }
      });
    }
    return result;
  }

  _onSuccessful(response?) {
    // I saw subclasses are calling this method - but no implementation existed in old framework
    // just adding the method here to make TypeScript happy.
  }

  //-------------------------------------------------Helpers------------------------------------------------------------
  _isFunctionName(value) {
    return endsWith(value, '()');
  }

  _isArray(obj) {
    return obj.constructor === Array;
  }

  findWizardService(parent) {
    if (parent) {
      if (parent.defaultId && parent.defaultId === 'wizardService') {
        return parent;
      } else {
        return this.findWizardService(parent.parent);
      }
    } else {
      return null;
    }
  }

  // registers an update to value of specific component.
  // per that information, the back/next navigation logic will be enabled/disabled
  // also takes to update the value in final output object/with a condition that this value is
  // embedded in output object, 'meta_data' array.
  metaDataValueChange(componentId, key, newValue, isValid = true) {
    if (!newValue) {
      // to denote the fact value should be explicitly removed from output of current step/wizard
      newValue = REMOVED;
    }
    this._changeMetaDataValue(key, newValue);
    this.stateOfExtendedControls[componentId] = {
      key,
      value: newValue,
      isMeta: true,
      isValid
    };
  }

  // please do NOT add any IFs and BUTs, specific to your wizard here!
  // this is very, very wrong...
  dataValueChange(componentId, key, newValue, isValid = true) {
    if (
      key === 'platform' &&
      this.output.agentType &&
      (this.output.agentType.id === 'troubleshooting' || this.output.agentType.id === 'windows') &&
      newValue === null
    ) {
      // yes, this is an example of IF, which should not been placed here..
      return;
    }

    if (!newValue) {
      // to denote the fact value should be explicitly removed from output of current step/wizard
      newValue = REMOVED;
    }
    this._changeSimpleValue(key, newValue);
    this.stateOfExtendedControls[componentId] = {
      key,
      value: newValue,
      isMeta: false,
      isValid
    };
  }

  _changeSimpleValue(key, newValue) {
    this.output[key] = newValue;
  }
}
